/**
 * Correction deduplication service
 * Removes duplicate corrections and handles position calculation
 */

import type { Correction } from '../types/grammar';

/**
 * Service for deduplicating and managing corrections
 */
export class CorrectionDeduplicator {
  /**
   * Remove duplicate corrections
   * @param corrections - Array of corrections
   * @returns Deduplicated corrections
   */
  removeDuplicates(corrections: Correction[]): Correction[] {
    if (!corrections || corrections.length === 0) {
      return [];
    }

    const correctionMap = new Map<string, Correction>();
    
    corrections.forEach(correction => {
      const key = this.generateCorrectionKey(correction);
      
      if (correctionMap.has(key)) {
        // Merge with existing correction
        const existing = correctionMap.get(key)!;
        correctionMap.set(key, this.mergeCorrections(existing, correction));
      } else {
        // Add new correction
        correctionMap.set(key, this.normalizeCorrection(correction));
      }
    });
    
    return Array.from(correctionMap.values());
  }

  /**
   * Remove overlapping corrections
   * @param corrections - Array of corrections
   * @param text - Original text for position calculation
   * @returns Non-overlapping corrections
   */
  removeOverlapping(corrections: Correction[], text: string): Correction[] {
    if (!corrections || corrections.length === 0) {
      return [];
    }

    // Calculate positions for all corrections
    const withPositions = corrections.map(c => 
      this.ensurePosition(c, text)
    );

    // Sort by position
    withPositions.sort((a, b) => {
      const posA = a.position?.start || 0;
      const posB = b.position?.start || 0;
      return posA - posB;
    });

    const result: Correction[] = [];
    let lastEnd = -1;

    withPositions.forEach(correction => {
      if (!correction.position) {
        // No position, include it
        result.push(correction);
        return;
      }

      const start = correction.position.start;
      const end = correction.position.end;

      // Check for overlap
      if (start >= lastEnd) {
        // No overlap
        result.push(correction);
        lastEnd = end;
      } else if (end > lastEnd) {
        // Partial overlap - check which correction is more important
        const lastCorrection = result[result.length - 1];
        if (this.shouldReplace(lastCorrection, correction)) {
          // Replace the last correction
          result[result.length - 1] = correction;
          lastEnd = end;
        }
      }
      // Else: fully contained, skip
    });

    return result;
  }

  /**
   * Calculate positions for corrections that don't have them
   * @param corrections - Array of corrections
   * @param text - Original text
   * @returns Corrections with positions
   */
  calculatePositions(corrections: Correction[], text: string): Correction[] {
    return corrections.map(correction => 
      this.ensurePosition(correction, text)
    );
  }

  /**
   * Merge corrections from multiple batches
   * @param batchResults - Array of correction arrays from different batches
   * @param offsets - Offset positions for each batch
   * @returns Merged corrections
   */
  mergeBatchCorrections(
    batchResults: Correction[][],
    offsets: number[]
  ): Correction[] {
    const allCorrections: Correction[] = [];

    batchResults.forEach((batch, index) => {
      const offset = offsets[index] || 0;
      
      batch.forEach(correction => {
        const adjustedCorrection = { ...correction };
        
        // Adjust position if present
        if (correction.position) {
          adjustedCorrection.position = {
            start: correction.position.start + offset,
            end: correction.position.end + offset
          };
        }
        
        allCorrections.push(adjustedCorrection);
      });
    });

    // Remove duplicates from overlapping regions
    return this.removeDuplicates(allCorrections);
  }

  /**
   * Find all positions of a text in the content
   * @param text - Full text
   * @param searchText - Text to find
   * @returns Array of positions
   */
  findAllPositions(text: string, searchText: string): number[] {
    const positions: number[] = [];
    let index = text.indexOf(searchText);
    
    while (index !== -1) {
      positions.push(index);
      index = text.indexOf(searchText, index + 1);
    }
    
    return positions;
  }

  /**
   * Generate a key for correction deduplication
   * @param correction - Correction to generate key for
   * @returns Unique key
   */
  private generateCorrectionKey(correction: Correction): string {
    // Use original text and position if available
    if (correction.position) {
      return `${correction.original}_${correction.position.start}_${correction.position.end}`;
    }
    return correction.original;
  }

  /**
   * Merge two corrections
   * @param existing - Existing correction
   * @param newCorrection - New correction to merge
   * @returns Merged correction
   */
  private mergeCorrections(
    existing: Correction,
    newCorrection: Correction
  ): Correction {
    // Combine correction suggestions
    const existingSuggestions = Array.isArray(existing.corrected) 
      ? existing.corrected 
      : [existing.corrected];
    const newSuggestions = Array.isArray(newCorrection.corrected) 
      ? newCorrection.corrected 
      : [newCorrection.corrected];
    
    const combinedSuggestions = [...new Set([...existingSuggestions, ...newSuggestions])];
    
    // Filter out empty suggestions and suggestions that match the original
    const filteredSuggestions = combinedSuggestions.filter(suggestion => 
      suggestion && 
      suggestion.trim() !== '' && 
      suggestion !== existing.original
    );

    return {
      ...existing,
      corrected: filteredSuggestions.length > 0 ? filteredSuggestions : existing.corrected,
      help: existing.help || newCorrection.help || 'Grammar correction',
      // Merge additional fields
      category: existing.category || newCorrection.category,
      examples: this.mergeArrays(existing.examples, newCorrection.examples),
      ruleArticle: existing.ruleArticle || newCorrection.ruleArticle,
      // Keep the most specific position
      position: existing.position || newCorrection.position
    };
  }

  /**
   * Normalize a correction
   * @param correction - Correction to normalize
   * @returns Normalized correction
   */
  private normalizeCorrection(correction: Correction): Correction {
    const suggestions = Array.isArray(correction.corrected) 
      ? correction.corrected 
      : [correction.corrected];
    
    // Filter out invalid suggestions
    const validSuggestions = suggestions.filter(s => 
      s && s.trim() !== '' && s !== correction.original
    );

    return {
      ...correction,
      corrected: validSuggestions.length > 0 ? validSuggestions : suggestions,
      help: correction.help || 'Grammar correction'
    };
  }

  /**
   * Ensure correction has position information
   * @param correction - Correction to check
   * @param text - Text to search in
   * @returns Correction with position
   */
  private ensurePosition(correction: Correction, text: string): Correction {
    if (correction.position) {
      return correction;
    }

    const positions = this.findAllPositions(text, correction.original);
    
    if (positions.length > 0) {
      // Use the first occurrence
      return {
        ...correction,
        position: {
          start: positions[0],
          end: positions[0] + correction.original.length
        }
      };
    }

    return correction;
  }

  /**
   * Determine if a correction should replace another
   * @param existing - Existing correction
   * @param candidate - Candidate correction
   * @returns True if candidate should replace existing
   */
  private shouldReplace(existing: Correction, candidate: Correction): boolean {
    // Prioritize corrections with more information
    const existingScore = this.scoreCorrectionImportance(existing);
    const candidateScore = this.scoreCorrectionImportance(candidate);
    
    return candidateScore > existingScore;
  }

  /**
   * Score correction importance
   * @param correction - Correction to score
   * @returns Importance score
   */
  private scoreCorrectionImportance(correction: Correction): number {
    let score = 0;
    
    // More suggestions is better
    if (Array.isArray(correction.corrected)) {
      score += correction.corrected.length;
    }
    
    // Having help text is important
    if (correction.help && correction.help !== 'Grammar correction') {
      score += 2;
    }
    
    // Category information adds value
    if (correction.category) {
      score += 1;
    }
    
    // Examples are valuable
    if (correction.examples && correction.examples.length > 0) {
      score += correction.examples.length;
    }
    
    // Rule article reference is valuable
    if (correction.ruleArticle) {
      score += 1;
    }
    
    return score;
  }

  /**
   * Merge two arrays, removing duplicates
   * @param arr1 - First array
   * @param arr2 - Second array
   * @returns Merged array
   */
  private mergeArrays<T>(arr1?: T[], arr2?: T[]): T[] | undefined {
    if (!arr1 && !arr2) return undefined;
    if (!arr1) return arr2;
    if (!arr2) return arr1;
    
    return [...new Set([...arr1, ...arr2])];
  }

  /**
   * Group corrections by category
   * @param corrections - Array of corrections
   * @returns Corrections grouped by category
   */
  groupByCategory(corrections: Correction[]): Map<string, Correction[]> {
    const groups = new Map<string, Correction[]>();
    
    corrections.forEach(correction => {
      const category = correction.category || 'uncategorized';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(correction);
    });
    
    return groups;
  }

  /**
   * Sort corrections by position
   * @param corrections - Array of corrections
   * @returns Sorted corrections
   */
  sortByPosition(corrections: Correction[]): Correction[] {
    return [...corrections].sort((a, b) => {
      const posA = a.position?.start || Number.MAX_SAFE_INTEGER;
      const posB = b.position?.start || Number.MAX_SAFE_INTEGER;
      return posA - posB;
    });
  }
}